import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  HistoryOutlined,
  UserOutlined,
  DollarOutlined,
  CrownOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import type { MenuProps } from 'antd';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  // 同步折叠状态到 body，便于主体区域根据侧栏宽度自适应
  useEffect(() => {
    const body = document.body;
    // 标记页面存在侧边栏（用于只在有侧栏时才应用主区域左边距）
    body.classList.add('has-sidebar');

    if (collapsed) {
      body.classList.add('sidebar-collapsed');
    } else {
      body.classList.remove('sidebar-collapsed');
    }
    return () => {
      body.classList.remove('sidebar-collapsed');
      body.classList.remove('has-sidebar');
    };
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems: MenuItem[] = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: '首页',
      onClick: () => navigate('/')
    },
    {
      key: '/video',
      icon: <VideoCameraOutlined />,
      label: '视频生成',
      onClick: () => navigate('/video')
    },
    {
      key: '/image',
      icon: <PictureOutlined />,
      label: '图像生成',
      onClick: () => navigate('/image')
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: '历史记录',
      onClick: () => navigate('/history')
    },
    {
      key: '/payment',
      icon: <DollarOutlined />,
      label: '积分充值',
      onClick: () => navigate('/payment')
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => navigate('/profile')
    },
    ...(user?.role === 'ADMIN' ? [{
      key: '/admin',
      icon: <CrownOutlined />,
      label: <span className="font-semibold text-amber-600">管理后台</span>,
      onClick: () => navigate('/admin')
    }] : []),
    {
      type: 'divider' as const
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: handleLogout
    }
  ];

  return (
    <Sider
      width={240}
      collapsedWidth={80}
      collapsed={collapsed}
      className="hidden lg:block app-sider bg-white shadow-xl transition-all duration-300"
      style={{
        overflow: 'hidden auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        backdropFilter: 'saturate(180%) blur(6px)'
      }}
    >
      {/* Logo区域 */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-white/70">
        {!collapsed ? (
          <div className="flex items-center space-x-2">
            <VideoCameraOutlined className="text-3xl text-blue-600" />
            <span className="text-xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent tracking-wide">
              Sora2 AI
            </span>
          </div>
        ) : (
          <VideoCameraOutlined className="text-3xl text-blue-600 mx-auto" />
        )}
        
        {/* 折叠按钮 */}
        <Tooltip title={collapsed ? '展开菜单' : '折叠菜单'} placement="right">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
          />
        </Tooltip>
      </div>

      {/* 用户信息 */}
      {user && (
        <div className={`p-4 border-b border-gray-200 ${collapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {user.email?.[0]?.toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">
                  {user.username || user.email}
                </div>
                <div className="text-xs text-gray-500 flex items-center space-x-1">
                  <DollarOutlined />
                  <span>{user.pointsBalance || 0} 积分</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 导航菜单 */}
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        className="border-0 app-sider-menu"
        inlineCollapsed={collapsed}
        style={{ 
          paddingTop: '8px',
          paddingBottom: '8px',
          backgroundColor: 'transparent'
        }}
      />

      {/* 底部信息 */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-500 text-center">
          {!collapsed ? (
            <div>v{__APP_VERSION__} © 2025 Sora2 AI</div>
          ) : (
            <div>v{__APP_VERSION__.split('.').slice(0, 2).join('.')}</div>
          )}
        </div>
      </div>
    </Sider>
  );
};

export default Sidebar;

